// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/components/Modals/CriarNegociacaoArquivo/BlocoDaVerbaNoArquivo.tsx

import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { RadioGroup } from 'rsuite';

import { Radio } from '../../../../../../../../components/Radio';
import { lerPreviaDoArquivo } from '../../../NegociacaoFornecedor.services';
import {
  OPCOES_DAS_LINHAS_SEM_VERBA,
  TEXTO_DA_VERBA_NO_ARQUIVO,
  aLeituraChegou,
  descricaoDaOpcaoDaVerba,
  escolherODestinoDaVerba,
  escolherODestinoDasVazias,
  escolhasDoEnvio,
  estadoInicialDaVerbaNoArquivo,
  linhaDaLeituraDoArquivo,
  mostraASubPergunta,
  mostraOAvisoDaConversao,
  opcoesDaVerbaNoArquivo,
  rotuloDasLinhasSemVerba,
  textoDeApoioDaVerbaNoArquivo,
  tituloDaOpcaoDaVerba,
  tituloDaOpcaoDasVazias,
} from '../../../verbaNoArquivo';
import type {
  DestinoDaVerbaDoArquivo,
  DestinoDasLinhasSemVerba,
  EscolhasDaVerbaNoArquivo,
  EstadoDaVerbaNoArquivo,
  LeituraPreviaDoArquivo,
} from '../../../verbaNoArquivo';
import styles from './BlocoDaVerbaNoArquivo.module.scss';

/** O que a leitura e a escolha valem para quem desenha o cartão do arquivo e monta o envio. */
export type VerbaNoArquivo = {
  leitura: LeituraPreviaDoArquivo | null;
  /** A leitura foi pedida e ainda não voltou. É o estado de leitura do cartão. */
  lendo: boolean;
  estado: EstadoDaVerbaNoArquivo;
  escolherODestino: (destino: DestinoDaVerbaDoArquivo) => void;
  escolherAsVazias: (destino: DestinoDasLinhasSemVerba) => void;
  /** O que viaja no envio, ou nada enquanto não houver leitura. */
  escolhas: EscolhasDaVerbaNoArquivo | null;
  /** A linha do cartão do arquivo: o que o sistema entendeu, ou o aviso de que ainda está lendo. */
  linhaDoCartao: string | null;
};

/**
 * A identidade do arquivo escolhido.
 *
 * O objeto do arquivo muda de referência a cada nova escolha, e não serve de chave de consulta. Nome,
 * tamanho e data de alteração identificam o arquivo sem abrir a planilha.
 */
const chaveDoArquivo = (file: File | null | undefined): string | null =>
  file ? `${file.name}|${file.size}|${file.lastModified}` : null;

/**
 * A leitura prévia do arquivo, e a escolha do comprador sobre a verba.
 *
 * **Trocar de arquivo esquece a escolha anterior:** o destino escolhido para o primeiro arquivo
 * pode nem ser oferecido pelo segundo.
 *
 * A leitura não grava nada do outro lado, e por isso o comprador pode trocar de arquivo à vontade.
 */
export const useVerbaNoArquivo = (file: File | null | undefined): VerbaNoArquivo => {
  const chave = chaveDoArquivo(file);

  const { data, isFetching } = useQuery({
    queryKey: ['negociacao/leitura-previa-do-arquivo', chave],
    enabled: Boolean(file),
    retry: false,
    // A leitura vale enquanto o arquivo for o mesmo: ele não muda sozinho, e reler devolveria os
    // mesmos números.
    staleTime: Infinity,
    queryFn: () => lerPreviaDoArquivo(file as File),
  });

  const leitura = aLeituraChegou(data) ? (data as LeituraPreviaDoArquivo) : null;
  const lendo = Boolean(file) && isFetching;

  const [escolhido, setEscolhido] = useState<EstadoDaVerbaNoArquivo | null>(null);

  useEffect(() => {
    setEscolhido(null);
  }, [chave]);

  const estado = escolhido ?? estadoInicialDaVerbaNoArquivo(leitura);

  const escolherODestino = useCallback(
    (destino: DestinoDaVerbaDoArquivo) => {
      setEscolhido(anterior => escolherODestinoDaVerba(anterior ?? estadoInicialDaVerbaNoArquivo(leitura), destino));
    },
    [leitura],
  );

  const escolherAsVazias = useCallback(
    (destino: DestinoDasLinhasSemVerba) => {
      setEscolhido(anterior => escolherODestinoDasVazias(anterior ?? estadoInicialDaVerbaNoArquivo(leitura), destino));
    },
    [leitura],
  );

  const linhaDoCartao = lendo ? TEXTO_DA_VERBA_NO_ARQUIVO.LENDO_O_ARQUIVO : leitura ? linhaDaLeituraDoArquivo(leitura) : null;

  return {
    leitura,
    lendo,
    estado,
    escolherODestino,
    escolherAsVazias,
    escolhas: escolhasDoEnvio(leitura, estado),
    linhaDoCartao,
  };
};

export type BlocoDaVerbaNoArquivoProps = {
  leitura: LeituraPreviaDoArquivo | null;
  estado: EstadoDaVerbaNoArquivo;
  onDestino: (destino: DestinoDaVerbaDoArquivo) => void;
  onDestinoDasVazias: (destino: DestinoDasLinhasSemVerba) => void;
  /**
   * O que separa os grupos de rádio de um modal dos do outro.
   *
   * Rádios de mesmo nome no mesmo documento formam um grupo só: sem nome próprio por bloco, marcar
   * num modal apagaria a marca do outro.
   */
  idDoGrupo: string;
  className?: string;
};

/**
 * A seção que pergunta o que fazer com a verba do arquivo.
 *
 * **É um componente, usado pelos dois modais de arquivo**, para as duas telas não divergirem.
 *
 * **Só existe depois de a leitura voltar:** mostrar as opções antes de saber se a coluna existe é
 * oferecer uma escolha que pode não existir naquele arquivo.
 *
 * As opções são um grupo de rádio controlado por valor, e não caixas de marcar: a escolha é de
 * seleção única, e a marca de selecionado da caixa solta não chega ao DOM.
 */
export const BlocoDaVerbaNoArquivo = ({
  leitura,
  estado,
  onDestino,
  onDestinoDasVazias,
  idDoGrupo,
  className,
}: BlocoDaVerbaNoArquivoProps): JSX.Element | null => {
  if (!aLeituraChegou(leitura)) return null;

  return (
    <section className={classNames(styles['bloco'], className)}>
      <p className={styles['cabecalho']}>{TEXTO_DA_VERBA_NO_ARQUIVO.CABECALHO}</p>
      <p className={styles['apoio']}>{textoDeApoioDaVerbaNoArquivo(leitura)}</p>

      {/*
        Onde a coluna vai fica FORA do aviso azul, que só aparece quando a coluna existe. Quem precisa
        de saber a posição é justamente quem mandou arquivo sem ela -- dentro do aviso, a frase nunca
        chegaria a esse comprador, e a tela continuaria sem dizer como montar a coluna.
      */}
      <p className={styles['apoio']}>{TEXTO_DA_VERBA_NO_ARQUIVO.ONDE_A_COLUNA_DE_VERBA_VAI}</p>

      {mostraOAvisoDaConversao(leitura) ? (
        <p className={styles['aviso-informativo']}>
          <span aria-hidden="true" className={styles['aviso-icone']}>
            ⓘ
          </span>
          {TEXTO_DA_VERBA_NO_ARQUIVO.AVISO_DA_CONVERSAO}
        </p>
      ) : null}

      <RadioGroup
        name={`verba-no-arquivo-destino-${idDoGrupo}`}
        value={estado.destino}
        onChange={(valor: DestinoDaVerbaDoArquivo) => onDestino(valor)}
        className={styles['opcoes']}
      >
        {opcoesDaVerbaNoArquivo(leitura).map(destino => (
          <Radio key={destino} value={destino} isSelected={estado.destino === destino} className={styles['opcao']}>
            <span className={styles['opcao-titulo']}>{tituloDaOpcaoDaVerba(destino)}</span>
            <span className={styles['opcao-descricao']}>{descricaoDaOpcaoDaVerba(destino)}</span>
          </Radio>
        ))}
      </RadioGroup>

      {mostraASubPergunta(leitura, estado) ? (
        <div className={styles['sub-pergunta']}>
          <p className={styles['sub-pergunta-rotulo']}>{rotuloDasLinhasSemVerba(leitura)}</p>

          <RadioGroup
            name={`verba-no-arquivo-vazias-${idDoGrupo}`}
            value={estado.destinoDasVazias}
            onChange={(valor: DestinoDasLinhasSemVerba) => onDestinoDasVazias(valor)}
            className={styles['opcoes']}
          >
            {OPCOES_DAS_LINHAS_SEM_VERBA.map(destino => (
              <Radio key={destino} value={destino} isSelected={estado.destinoDasVazias === destino} className={styles['opcao']}>
                <span className={styles['opcao-titulo']}>{tituloDaOpcaoDasVazias(destino)}</span>
              </Radio>
            ))}
          </RadioGroup>
        </div>
      ) : null}
    </section>
  );
};
